<script lang="ts">
    import { onMount, tick } from 'svelte'

    import Icon from '@iconify/svelte'
    import EasyMDE from 'easymde/src/js/easymde'

    import Modal from 'hydria/components/Modal.svelte'
    import { type TGettext, gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import { type SimpleFetchResult, simpleFetch } from 'hydria/services/session'

    interface Props {
        saved: string
        value: string
        disabled?: boolean
        showLabel?: boolean
        id?: string | null
        fieldName?: string | null
        label?: any
        helpText?: TGettext | 'default' | undefined | null
        isValid?: any

        save?: (newValue: string) => void
    }

    let { saved: savedValue, value: currentValue = $bindable(), disabled = false, showLabel = true, id = $bindable(null), fieldName = null, label = tgettext('Introduction'), helpText = 'default', isValid = (value: string) => true, save = (v) => {} }: Props = $props()

    let value: string = $state('')

    if (id == null) {
        id = 'consent-mde-' + Math.floor(Math.random() * 1000000000) + String(Date.now())
    }

    function load(currentValue: string) {
        if (value !== currentValue) value = currentValue
    }

    if (helpText === 'default') helpText = tgettext('Ce texte est affiché au début de la page. Ne pas oublier d’y inclure le titre principal.')

    let isOpen = $state(false)

    function keep() {
        currentValue = value
        save(currentValue)
        close()
    }

    function open() {
        load(currentValue)
        isOpen = true
    }
    function close() {
        load(currentValue)
        isOpen = false
        mde = null
        mdeElement = undefined
    }

    let mdeElement: HTMLTextAreaElement | undefined = $state()
    let mde: any = $state(null)

    function syncRevToMde(v: string) {
        const cv = mde.value()
        if (cv !== v) mde.value(v)
    }

    let previewHtml: string | null = $state(null)
    let loadingPreview = $state(false)
    async function loadPreview(field: string, value: string) {
        let r: SimpleFetchResult
        loadingPreview = true
        try {
            r = await simpleFetch('POST', '/json/consent/1/markdown', { text: value })
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            previewHtml = '<p>' + $_('Erreur de chargement de la prévisualisation') + '</p>'
        } else {
            previewHtml = r.data.html
        }
        loadingPreview = false
    }

    onMount(() => {
        load(currentValue)
    })
    let savedIsValid = $derived(isValid(savedValue))
    let isPristine = $derived(currentValue === savedValue)
    let statusIcon: string = $derived(isPristine ? (savedIsValid ? 'mdi:check-circle' : 'mdi:alert') : isValid(currentValue) ? 'mdi:undo' : 'mdi:undo')
    let statusClass: string = $derived(isPristine ? (savedIsValid ? 'btn-link' : 'btn-danger') : isValid(currentValue) ? 'btn-outline-warning' : 'btn-outline-danger')

    $effect(() => {
        if (isOpen && mdeElement && !mde) {
            tick().then(() => {
                mde = new EasyMDE({
                    element: mdeElement,
                    // Should be false, because we don't want font awesome.. but this editor
                    // and it's predecessor SimpleMDE are based on this shit.
                    autoDownloadFontAwesome: true,
                    indentWithTabs: false,
                    tabSize: 4,
                    maxHeight: '70vh',
                    toolbar: [
                        'bold',
                        'italic', // 'strikethrough',
                        'heading',
                        '|', //'code',
                        'quote',
                        'unordered-list',
                        'ordered-list',
                        // 'clean-block',
                        '|',
                        // {
                        //     name: 'attachment'
                        //     action: (editor) ->
                        //         $scope.chooseAttachments('smde', editor, 'markdown')
                        //     className: 'fa fa-picture-o'
                        //     title: Gettext.gettext('Insert link to files and images')
                        //     // **FIXME** Sur version 2.0, on devrait pouvoir setter un shortcut
                        //     // utiliser Cmd-Y
                        // },
                        // 'link', 'image',
                        'table', // 'horizontal-rule',
                        // '|', // **PREVIEW** ne marche pas pour l’immédiat, juste oublier le temps de passer à autre chose
                        // 'preview',
                        // 'side-by-side',
                        // 'fullscreen',
                        // '|',
                        // {
                        //     name: "guide",
                        //     action: "/#/hydria-help/9593f60e-1bcc-4e4e-af21-92f921852284",
                        //     className: "fa fa-question-circle",
                        //     title: "Markdown Guide",
                        //     default: true
                        // },
                        // 'undo', 'redo'
                    ],
                })
                mde.codemirror.on('change', () => {
                    value = mde.value()
                })
            })
        }
    })
    $effect(() => {
        if (mde) syncRevToMde(value)
    })
    $effect(() => {
        if (fieldName && !isOpen && currentValue) loadPreview(fieldName, currentValue)
    })
</script>

<style>
    /* The table button is destroyed by Bootstrap */
    .mde-container :global(.table) {
        width: auto;
    }
</style>

<div class="row g-3 align-items-baseline" style="overflow: hidden">
    {#if showLabel}
        <div class="col-2 text-end"><label for="nothing" class="col-form-label" onclick={() => open()} aria-hidden="true">{$resolve(label)}</label></div>
    {/if}
    <div class="col" style="overflow: hidden">
        {#if isOpen}
            <Modal close={() => close()} keep={() => keep()} modalClasses="modal-xl mde-modal">
                <div class="modal-header text-bg-primary">{$resolve(label)}</div>
                <div class="modal-body">
                    <div class="mde-container"><textarea {id} bind:this={mdeElement} bind:value></textarea></div>
                    {#if helpText != null}
                        <div class="form-text">{$resolve(helpText)}</div>
                    {/if}
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => close()}>{$_('Fermer')}</button>
                    <button type="button" class="btn btn-sm btn-primary" disabled={!isValid(value) || value === currentValue} onclick={() => keep()}>{$_('Appliquer')}</button>
                </div>
            </Modal>
        {/if}
        <div aria-hidden="true" ondblclick={() => open()} class="scheme scheme-display">
            {#if currentValue && currentValue != ''}
                <div class:text-muted={loadingPreview}>{@html previewHtml == null ? '…' : previewHtml}</div>
            {:else}
                <em class="text-muted">{$_('non-configuré')}</em>
            {/if}
        </div>
        {#if helpText != null}
            <div class="form-text">{$resolve(helpText)}</div>
        {/if}
    </div>
    <div class="col-auto text-end">
        <button
            type="button"
            class="btn btn-sm {statusClass}"
            disabled={isPristine}
            onclick={() => {
                currentValue = savedValue
            }}><Icon inline icon={statusIcon} /></button
        >
        <button type="button" class="btn btn-sm btn-outline-secondary" {disabled} onclick={() => open()}><Icon inline icon="mdi:edit" /></button>
    </div>
</div>
